// ═══════════════════════════════════════════════════════════
// نظام ERP - جمعية الدعوة بشمال الرياض
// LoginPage.jsx — تصميم عصري
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, ArrowLeft, Mail, AlertCircle } from "lucide-react";
import { C, ORG, fmt } from "./constants";
import { loginUser, rpc } from "./supabaseClient";

const AnimatedLogo = () => (
  <svg width="56" height="56" viewBox="0 0 100 100" fill="none" style={{ filter: "drop-shadow(0 4px 20px rgba(220,193,96,0.4))" }}>
    <rect x="10" y="15" width="65" height="65" rx="6" stroke="#dcc160" strokeWidth="2.5" fill="none" opacity="0.6" />
    <path d="M40 72 C40 50 28 46 33 28 C36 20 40 16 40 16 C40 16 44 20 47 28 C52 46 40 50 40 72Z" fill="#dcc160">
      <animate attributeName="d" values="M40 72 C40 50 28 46 33 28 C36 20 40 16 40 16 C40 16 44 20 47 28 C52 46 40 50 40 72Z;M40 72 C40 52 30 48 34 30 C37 22 40 17 40 17 C40 17 43 22 46 30 C50 48 40 52 40 72Z;M40 72 C40 50 28 46 33 28 C36 20 40 16 40 16 C40 16 44 20 47 28 C52 46 40 50 40 72Z" dur="3s" repeatCount="indefinite" />
    </path>
    <path d="M52 68 C52 50 42 46 46 32 C48 26 52 22 52 22 C52 22 56 26 58 32 C62 46 52 50 52 68Z" fill="#dcc160" opacity="0.7">
      <animate attributeName="opacity" values="0.7;0.5;0.7" dur="2.5s" repeatCount="indefinite" />
    </path>
    <path d="M62 65 C62 52 55 48 58 38 C59 33 62 30 62 30 C62 30 65 33 66 38 C69 48 62 52 62 65Z" fill="#00b4a2" opacity="0.5">
      <animate attributeName="opacity" values="0.5;0.3;0.5" dur="2s" repeatCount="indefinite" />
    </path>
  </svg>
);

export default function LoginPage({ onLogin }) {
  // الأرقام من الإعدادات (app_settings) عبر app_public_stats — متاحة قبل تسجيل الدخول (BUG-050)
  const [stats, setStats] = useState({});
  useEffect(() => { rpc("app_public_stats").then((r) => setStats(r || {})).catch(() => {}); }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusField, setFocusField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("يرجى إدخال البريد وكلمة المرور"); return; }
    setLoading(true); setError("");
    try {
      const user = await loginUser(email, password);
      onLogin(user);
    } catch (err) {
      setError(err.message || "بيانات غير صحيحة");
    }
    setLoading(false);
  };

  const inputStyle = (field) => ({
    width: "100%", padding: "14px 44px", border: "none", borderRadius: 0,
    fontSize: 14, fontFamily: "inherit", outline: "none", background: "transparent",
    color: C.primary, borderBottom: `2px solid ${focusField === field ? "#dcc160" : C.border}`,
    transition: "border-color 0.3s",
  });

  return (
    <div style={{ direction: "rtl", fontFamily: "'Readex Pro', sans-serif", minHeight: "100vh", display: "flex", background: C.primary }}>

      {/* الجانب الأيسر — الغرافيك */}
      <div className="login-side" style={{
        flex: "0 0 45%", position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(160deg, #003d58 0%, #001a28 100%)`,
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.06 }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: "absolute", width: 2, height: "140%", background: "#dcc160",
              right: `${10 + i * 12}%`, top: "-20%", transform: `rotate(${15 + i * 3}deg)`,
            }} />
          ))}
        </div>

        <div style={{ textAlign: "center", position: "relative", zIndex: 2, padding: 40 }}>
          <AnimatedLogo />
          <h1 style={{ fontSize: 30, fontWeight: 700, color: "#dcc160", marginTop: 20, letterSpacing: -0.5 }}>
            دعوة شمال الرياض
          </h1>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 8, maxWidth: 260, margin: "8px auto 0" }}>
            نظام الإدارة المتكامل لجمعية الدعوة والإرشاد وتوعية الجاليات
          </p>

          <div style={{ marginTop: 48, display: "flex", gap: 24, justifyContent: "center", color: "rgba(255,255,255,0.3)", fontSize: 11 }}>
            <div><div style={{ fontSize: 26, fontWeight: 700, color: "#dcc160" }}>{stats.new_muslims_total != null ? fmt(stats.new_muslims_total) : "—"}</div><div>مسلم جديد</div></div>
            <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
            <div><div style={{ fontSize: 26, fontWeight: 700, color: "#00b4a2" }}>{stats.departments_count ?? "—"}</div><div>إدارات</div></div>
            <div style={{ width: 1, background: "rgba(255,255,255,0.1)" }} />
            <div><div style={{ fontSize: 26, fontWeight: 700, color: "#dcc160" }}>{String(ORG.founded).replace(/[^\d]/g, "")}</div><div>هـ التأسيس</div></div>
          </div>
        </div>
      </div>

      {/* الجانب الأيمن — النموذج */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", padding: "40px 20px" }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <div className="mobile-logo" style={{ display: "none", textAlign: "center", marginBottom: 24 }}>
            <AnimatedLogo />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: C.primary, marginTop: 8 }}>دعوة شمال الرياض</h2>
          </div>

              <div style={{ marginBottom: 32 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: C.primary, margin: 0 }}>مرحباً بك</h2>
                <p style={{ fontSize: 13, color: C.textLight, marginTop: 6 }}>سجّل دخولك بالبريد الإلكتروني</p>
              </div>

              {error && (
                <div style={{
                  padding: "10px 14px", background: `${C.danger}08`, borderRight: `3px solid ${C.danger}`,
                  borderRadius: "0 8px 8px 0", marginBottom: 20, fontSize: 12, color: C.danger,
                  display: "flex", alignItems: "center", gap: 8,
                }}><AlertCircle size={14} /> {error}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 8, display: "block" }}>البريد الإلكتروني</label>
                  <div style={{ position: "relative" }}>
                    <Mail size={16} style={{ position: "absolute", insetInlineStart: 12, top: "50%", transform: "translateY(-50%)", color: focusField === "email" ? "#dcc160" : C.textLight, transition: "color 0.3s" }} />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@cnr.org.sa" onFocus={() => setFocusField("email")} onBlur={() => setFocusField(null)}
                      style={inputStyle("email")} />
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 8, display: "block" }}>كلمة المرور</label>
                  <div style={{ position: "relative" }}>
                    <Lock size={16} style={{ position: "absolute", insetInlineStart: 12, top: "50%", transform: "translateY(-50%)", color: focusField === "pass" ? "#dcc160" : C.textLight, transition: "color 0.3s" }} />
                    <input type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                      onFocus={() => setFocusField("pass")} onBlur={() => setFocusField(null)}
                      style={inputStyle("pass")} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ position: "absolute", insetInlineEnd: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 0 }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* استعادة كلمة المرور الذاتية موقوفة مؤقتاً (BUG-004) — إعادة التعيين من مدير النظام فقط */}
                <div style={{ textAlign: "end", marginBottom: 24, fontSize: 11, color: C.textLight }}>
                  نسيت كلمة المرور؟ تواصل مع مدير النظام
                </div>

                <button type="submit" disabled={loading} style={{
                  width: "100%", padding: "14px", border: "none", borderRadius: 10,
                  fontSize: 14, fontWeight: 700, fontFamily: "inherit",
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "#003d58", background: "#dcc160",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "all 0.3s", boxShadow: "0 4px 16px rgba(220,193,96,0.3)",
                }}>
                  <ArrowLeft size={18} />
                  {loading ? "جاري الدخول..." : "تسجيل الدخول"}
                </button>
              </form>

          <div style={{ marginTop: 32, textAlign: "center", fontSize: 9, color: C.textLight }}>
            © {new Date().getFullYear()} {ORG.shortName} • {ORG.website}
          </div>
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: #9aacb8; opacity: 0.6; }
        @media (max-width: 768px) { .login-side { display: none !important; } .mobile-logo { display: block !important; } }
      `}</style>
    </div>
  );
}
